import { z } from 'zod';

/**
 * **What a member of an organization may do** — one column on `org_membership`,
 * because privilege is a property of the membership and not of the person: one
 * person is an owner of the family office and read-only at the club.
 *
 * Not to be confused with the platform operator, who is the Auth0 `admin` RBAC
 * value, holds **no membership row at all**, and bypasses `OrgAccessGuard`
 * entirely. That is why the top level here is `owner` and not `admin`: two
 * different things must not share a word, and a guard that read an absent
 * membership as a level rather than as the operator would lock the operator out
 * of every organization in the system.
 */
export const MEMBERSHIP_LEVELS = [
  'owner',
  'standard_sensitive',
  'standard',
  'read_only_sensitive',
  'read_only',
] as const;

export const MembershipLevel = z.enum(MEMBERSHIP_LEVELS);
export type MembershipLevel = z.infer<typeof MembershipLevel>;

/**
 * **The five levels are not a total order**, which is the whole reason this file
 * derives two axes instead of publishing a ladder.
 *
 * `standard` writes notes and cannot read confidential notes; `read_only_sensitive`
 * reads confidential notes and cannot write. Neither dominates the other, so
 * there is no single rung a route could name that admits one and excludes the
 * other. A check of the form "at least `standard`" is meaningless across the set.
 *
 * What *is* ordered is writing, on its own. So:
 *
 *  - {@link writeAxisOf} answers the guard's question and nothing else.
 *  - {@link seesConfidential} answers a read question and never reaches a guard.
 *
 * Keeping them apart is what lets `WriteAccessGuard` stay ignorant of what a
 * confidential note is. It is the same split the schema already draws between
 * `fact.sensitivity` (the catalog's word about a fact) and
 * `prose_input.confidential` (a person's word about a note): two different
 * questions, one column for each.
 */
export const WRITE_AXIS = ['none', 'notes', 'all'] as const;
export const WriteAxis = z.enum(WRITE_AXIS);
export type WriteAxis = z.infer<typeof WriteAxis>;

const WRITE_AXIS_BY_LEVEL: Readonly<Record<MembershipLevel, WriteAxis>> = Object.freeze({
  owner: 'all',
  standard_sensitive: 'notes',
  standard: 'notes',
  read_only_sensitive: 'none',
  read_only: 'none',
});

const SEES_CONFIDENTIAL_BY_LEVEL: Readonly<Record<MembershipLevel, boolean>> = Object.freeze({
  owner: true,
  standard_sensitive: true,
  standard: false,
  read_only_sensitive: true,
  read_only: false,
});

/** How much of the record this level may change. The one thing the guard reads. */
export const writeAxisOf = (level: MembershipLevel): WriteAxis => WRITE_AXIS_BY_LEVEL[level];

/**
 * Whether this level reads notes the office marked confidential (ruled
 * 2026-09-19: on the record, out of research). A level that cannot read one
 * cannot file one either — a note its author could not read back is not a note
 * they can be allowed to write — and `InputsService.addInput` is where that is
 * refused.
 */
export const seesConfidential = (level: MembershipLevel): boolean =>
  SEES_CONFIDENTIAL_BY_LEVEL[level];

/**
 * Whether a caller holding `held` may make a write that needs `needed`.
 *
 * The comparison is by index into {@link WRITE_AXIS}, so `all` covers `notes`
 * and `notes` covers `none` without anybody writing the six cases out. A route
 * needing `none` is satisfied by every level, which is the honest reading of
 * "this route writes nothing a level could object to".
 */
export const writeAxisAllows = (held: WriteAxis, needed: WriteAxis): boolean =>
  WRITE_AXIS.indexOf(held) >= WRITE_AXIS.indexOf(needed);

/** What the admin SPA calls each level. American spelling, end-result words. */
export const MEMBERSHIP_LEVEL_LABELS: Readonly<Record<MembershipLevel, string>> = Object.freeze({
  owner: 'Owner',
  standard_sensitive: 'Standard — sensitive',
  standard: 'Standard',
  read_only_sensitive: 'Read-only — sensitive',
  read_only: 'Read-only',
});

/**
 * One sentence per level, for the picker that grants one. They say what the
 * member can do, not what they are denied: the office is choosing a seat for a
 * colleague, not reading a list of refusals.
 */
export const MEMBERSHIP_LEVEL_DESCRIPTIONS: Readonly<Record<MembershipLevel, string>> =
  Object.freeze({
    owner: 'Full use of the organization’s records, including research, imports and merges.',
    standard_sensitive:
      'Reads everything, including confidential notes, and can add notes and log conversations.',
    standard: 'Reads the records and can add notes and log conversations.',
    read_only_sensitive: 'Reads everything, including confidential notes. Changes nothing.',
    read_only: 'Reads the records. Changes nothing.',
  });
